import { getStaffSessionToken, staffSessionHeaders } from '../../auth/staffSession';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import logo from '../../../assets/images/farmstream_logo.png';
import { authSelector } from '../../auth/state/authSlice';
import { Button } from '../../../components/Button';
import {
  useDecodeShareQrMutation,
  useRegenerateShareQrMutation,
  useSaveCameraMutation,
  useListCamerasMutation,
  useUpdateCameraMutation,
  useDeleteCameraMutation,
  useRevealCameraPasswordMutation,
  useSavedCameraShareQrMutation,
  ShareQrDecodeResponse,
  ShareQrRegenerateResponse,
  SavedCamera
} from '../api/toolsApi';

/**
 * Team-only (/tools) utility page, reached after Google Workspace SSO via
 * /team-login. Decode a camera share QR, save the camera with a nickname +
 * notes (password encrypted server-side, never shown), then search saved
 * cameras and mint fresh share QRs from them.
 */
export const Tools = () => {
  const navigate = useNavigate();
  const { isAdmin, token } = useSelector(authSelector);
  const [decodeQr, { isLoading }] = useDecodeShareQrMutation();
  const [regenerateQr, { isLoading: isRegenerating }] = useRegenerateShareQrMutation();
  const [saveCamera, { isLoading: isSaving }] = useSaveCameraMutation();
  const [listCameras, { isLoading: isListing }] = useListCamerasMutation();
  const [updateCamera, { isLoading: isUpdating }] = useUpdateCameraMutation();
  const [deleteCamera] = useDeleteCameraMutation();
  const [revealPassword] = useRevealCameraPasswordMutation();
  const [savedShareQr, { isLoading: isMintingSavedQr }] = useSavedCameraShareQrMutation();

  // --- decoder state ---
  const [result, setResult] = useState<ShareQrDecodeResponse | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [simLookup, setSimLookup] = useState<{ iccid: string; provider_status: string } | null>(null);
  const [simError, setSimError] = useState('');
  const [simBusy, setSimBusy] = useState(false);
  const simAbort = useRef<AbortController | null>(null);
  const qrGeneration = useRef(0);
  useEffect(() => () => { simAbort.current?.abort(); simAbort.current = null; }, []);
  useEffect(() => {
    simAbort.current?.abort(); simAbort.current = null; setSimLookup(null); setSimError(''); setSimBusy(false);
  }, [token]);

  const [regenerated, setRegenerated] = useState<ShareQrRegenerateResponse | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- save form state ---
  const [nickname, setNickname] = useState('');
  const [notes, setNotes] = useState('');
  const [savedUid, setSavedUid] = useState<string | null>(null);

  // --- saved cameras state ---
  const [cameras, setCameras] = useState<SavedCamera[]>([]);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editNickname, setEditNickname] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [armedDelete, setArmedDelete] = useState<number | null>(null);
  const [busyCameraId, setBusyCameraId] = useState<number | null>(null);
  // QR minted for a saved camera, shown inline under its row
  const [rowQr, setRowQr] = useState<{ id: number; qr: ShareQrRegenerateResponse } | null>(null);
  // Passwords revealed per saved camera row (fetched on demand)
  const [revealed, setRevealed] = useState<Record<number, string>>({});

  const staffSession = useMemo(() => getStaffSessionToken() || '', []);

  // Without an admin session, bounce to team-login (Google Workspace SSO);
  // ?next brings them straight back here after signing in.
  useEffect(() => {
    if (!token || !isAdmin || !staffSession) {
      navigate('/team-login?next=/tools', { replace: true });
    }
  }, [token, isAdmin, staffSession, navigate]);

  const refreshCameras = useCallback(
    async (term: string) => {
      if (!staffSession) return;
      try {
        const res = await listCameras({  search: term || undefined }).unwrap();
        setCameras(res.cameras);
      } catch (err) {
        console.error(err);
        toast.error('Could not load saved cameras');
      }
    },
    [staffSession, listCameras]
  );

  // Load the saved list on arrival, and re-query as the search box changes.
  useEffect(() => {
    const timer = setTimeout(() => refreshCameras(search), 300);
    return () => clearTimeout(timer);
  }, [search, refreshCameras]);

  const onFile = useCallback(
    async (file: File) => {
      if (!staffSession) return;
      const generation = ++qrGeneration.current;
      simAbort.current?.abort(); simAbort.current = null; setSimLookup(null); setSimError(''); setSimBusy(false);
      if (!file.type.startsWith('image/')) {
        toast.error('Please drop an image file (photo or screenshot of the QR)');
        return;
      }
      setResult(null);
      setRegenerated(null);
      setSavedUid(null);
      setShowPassword(false);
      setNickname('');
      setNotes('');
      setPreview((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(file);
      });

      const formData = new FormData();
      formData.append('image', file);
      try {
        const res = await decodeQr(formData).unwrap();
        if (generation !== qrGeneration.current) return;
        setResult(res);
        toast.success(`Decoded share QR for ${res.uid}`);
      } catch (err: any) {
        console.error(err);
        toast.error(err?.data?.message || 'Could not decode a share QR from that image');
      }
    },
    [staffSession, decodeQr]
  );

  // Let the team paste a screenshot straight from the clipboard.
  useEffect(() => {
    const onPaste = (event: Event) => {
      const clipboard = (event as ClipboardEvent).clipboardData;
      const item = Array.from(clipboard?.items || []).find((i) => i.type.startsWith('image/'));
      const file = item?.getAsFile();
      if (file) onFile(file);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [onFile]);

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file) onFile(file);
  };

  const onRetrieveSim = async () => {
    if (!result || !token || simBusy) return;
    const generation = qrGeneration.current;
    const abort = new AbortController(); simAbort.current = abort;
    setSimBusy(true); setSimLookup(null); setSimError('');
    const timeout = setTimeout(() => abort.abort(), 150000);
    try {
      const response = await fetch('/tools/retrieve-sim-iccid', {
        method: 'POST', credentials: 'omit', cache: 'no-store', signal: abort.signal,
        headers: { ...staffSessionHeaders(), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: result.uid, camera_password: result.password })
      });
      let value = await response.json();
      if (generation !== qrGeneration.current || simAbort.current !== abort) return;
      if (!response.ok) {
        const messages: Record<string, string> = {
          staff_session_required: 'Sign in again with your Farmstream team account.',
          sim_lookup_not_available: 'SIM lookup is not yet enabled on the server.',
          sim_worker_offline: 'The lookup Mac is offline. Please wake it and try again.',
          sim_lookup_busy: 'Another camera is being checked. Please try again shortly.',
          camera_unavailable: 'Could not read the camera SIM. Check its power, connection and password, then try again.',
          camera_response_invalid: 'The camera did not return a valid SIM ICCID.'
        };
        setSimError(messages[value.error] || 'SIM lookup is temporarily unavailable. Please try again later.');
        return;
      }
      if (response.status === 202 && /^[a-f0-9]{32}$/.test(value.job_id)) {
        const jobId = value.job_id;
        while (value.state === 'queued' || value.state === 'running') {
          await new Promise<void>((resolve, reject) => {
            if (abort.signal.aborted) { reject(new Error('cancelled')); return; }
            const cancelled = () => { clearTimeout(timer); reject(new Error('cancelled')); };
            const timer = setTimeout(() => { abort.signal.removeEventListener('abort', cancelled); resolve(); }, 2000);
            abort.signal.addEventListener('abort', cancelled, { once: true });
          });
          const polled = await fetch(`/tools/sim-lookups/${jobId}`, {
            headers: { ...staffSessionHeaders(), Authorization: `Bearer ${token}` }, cache: 'no-store', credentials: 'omit', signal: abort.signal
          });
          value = await polled.json();
          if (!polled.ok) throw new Error('lookup_unavailable');
          if (generation !== qrGeneration.current || simAbort.current !== abort) return;
        }
        if (value.state !== 'complete') {
          setSimError(value.error === 'lookup_expired' ? 'The lookup timed out. Check the camera and lookup Mac are online, then try again.' : 'Could not read the camera SIM. Check its power, connection and password, then try again.');
          return;
        }
      }
      if (!/^89[0-9]{16,20}$/.test(value.iccid) || !['managed_pelion','not_found','unverified'].includes(value.provider_status)) {
        setSimError('The server returned an incomplete SIM result. Please try again.'); return;
      }
      setSimLookup(value);
    } catch {
      if (generation === qrGeneration.current && simAbort.current === abort) {
        setSimError('The lookup did not complete. Check the camera is online and try again.');
      }
    } finally {
      clearTimeout(timeout);
      if (simAbort.current === abort) { simAbort.current = null; setSimBusy(false); }
    }
  };

  const onRegenerate = async () => {
    if (!result || !staffSession || isRegenerating) return;
    try {
      const res = await regenerateQr({

        uid: result.uid,
        camera_password: result.password,
        username: result.username,
        identifier: result.identifier || undefined
      }).unwrap();
      setRegenerated(res);
      toast.success(`New share QR issued for ${res.uid}`);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || 'Could not generate a new share QR');
    }
  };

  const onSave = async () => {
    if (!result || !staffSession || isSaving) return;
    if (!nickname.trim()) {
      toast.error('Give the camera a nickname first');
      return;
    }
    try {
      const res = await saveCamera({

        uid: result.uid,
        camera_password: result.password,
        username: result.username,
        identifier: result.identifier || undefined,
        nickname: nickname.trim(),
        notes: notes.trim() || undefined
      }).unwrap();
      setSavedUid(res.camera.uid);
      toast.success(
        res.updated
          ? `Updated saved camera ${res.camera.nickname}`
          : `Saved ${res.camera.nickname} to the address book`
      );
      refreshCameras(search);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || 'Could not save the camera');
    }
  };

  const startEdit = (camera: SavedCamera) => {
    setEditingId(camera.id);
    setEditNickname(camera.nickname);
    setEditNotes(camera.notes);
  };

  const onUpdate = async () => {
    if (editingId === null || !staffSession || isUpdating) return;
    if (!editNickname.trim()) {
      toast.error('Nickname cannot be empty');
      return;
    }
    try {
      const res = await updateCamera({

        id: editingId,
        nickname: editNickname.trim(),
        notes: editNotes.trim()
      }).unwrap();
      setCameras((prev) => prev.map((c) => (c.id === res.camera.id ? res.camera : c)));
      setEditingId(null);
      toast.success('Camera updated');
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || 'Could not update the camera');
    }
  };

  const onDelete = async (camera: SavedCamera) => {
    if (!staffSession || busyCameraId) return;
    // Deleting loses the stored password — require a second click to confirm.
    if (armedDelete !== camera.id) {
      setArmedDelete(camera.id);
      setTimeout(() => setArmedDelete((cur) => (cur === camera.id ? null : cur)), 4000);
      return;
    }
    setArmedDelete(null);
    setBusyCameraId(camera.id);
    try {
      await deleteCamera({  id: camera.id }).unwrap();
      setCameras((prev) => prev.filter((c) => c.id !== camera.id));
      if (rowQr?.id === camera.id) setRowQr(null);
      toast.success(`Deleted ${camera.nickname}`);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || 'Could not delete the camera');
    } finally {
      setBusyCameraId(null);
    }
  };

  const onRevealPassword = async (camera: SavedCamera) => {
    if (!staffSession) return;
    if (revealed[camera.id] !== undefined) {
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[camera.id];
        return next;
      });
      return;
    }
    try {
      const res = await revealPassword({  id: camera.id }).unwrap();
      setRevealed((prev) => ({ ...prev, [camera.id]: res.password }));
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || 'Could not fetch the password');
    }
  };

  const onRowShareQr = async (camera: SavedCamera) => {
    if (!staffSession || isMintingSavedQr) return;
    if (rowQr?.id === camera.id) {
      setRowQr(null);
      return;
    }
    setBusyCameraId(camera.id);
    try {
      const res = await savedShareQr({  id: camera.id }).unwrap();
      setRowQr({ id: camera.id, qr: res });
      toast.success(`New share QR issued for ${camera.nickname}`);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || 'Could not generate a share QR');
    } finally {
      setBusyCameraId(null);
    }
  };

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  const formatWhen = (iso: string | null) => {
    if (!iso) return '';
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
  };

  return (
    <Page>
      <Card>
        <Header>
          <Logo src={logo} alt="Farmstream" />
          <div>
            <Title>Team tools</Title>
            <Subtitle>Internal utilities for the Farmstream team.</Subtitle>
          </div>
        </Header>

        <ToolCard>
          <ToolTitle>Share QR decoder</ToolTitle>
          <ToolDescription>
            Drop a photo or screenshot of a camera share QR (from the CamHi / Farmstream app) to
            recover the camera UID and password — then save the camera below so the team can find
            it again later.
          </ToolDescription>

          <DropZone
            active={dragActive}
            busy={isLoading}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}>
            {preview ? (
              <PreviewImage src={preview} alt="Uploaded QR" />
            ) : (
              <DropHint>
                <strong>Drop a QR image here</strong>
                <span>or click to browse — you can also paste a screenshot (Ctrl/Cmd+V)</span>
              </DropHint>
            )}
            {isLoading && <BusyOverlay>Decoding…</BusyOverlay>}
          </DropZone>
          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFile(file);
              e.target.value = '';
            }}
          />

          {result && (
            <ResultPanel>
              {result.expired && (
                <ExpiredBanner>
                  <span>
                    This share QR expired {formatWhen(result.expires_at)} (codes are valid for{' '}
                    {result.validity_hours}h). The recovered credentials may still work on the
                    camera.
                  </span>
                  {!regenerated && (
                    <Button onClick={onRegenerate} isLoading={isRegenerating}>
                      Regenerate new share QR
                    </Button>
                  )}
                </ExpiredBanner>
              )}
              {regenerated && (
                <NewQrPanel>
                  <NewQrTitle>
                    New share QR — valid until {formatWhen(regenerated.expires_at)}
                  </NewQrTitle>
                  <NewQrHint>
                    Scan it with the Farmstream / CamHi app on another phone, or download it to
                    send on.
                  </NewQrHint>
                  <NewQrImage
                    src={regenerated.qr_image}
                    alt={`New share QR for ${regenerated.uid}`}
                  />
                  <NewQrActions>
                    <DownloadLink
                      href={regenerated.qr_image}
                      download={`share-qr-${regenerated.uid}.png`}>
                      Download PNG
                    </DownloadLink>
                  </NewQrActions>
                </NewQrPanel>
              )}
              <ResultGrid>
                <ResultRow>
                  <ResultLabel>UID</ResultLabel>
                  <ResultValue>{result.uid}</ResultValue>
                  <CopyButton onClick={() => copy('UID', result.uid)}>Copy</CopyButton>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>Username</ResultLabel>
                  <ResultValue>{result.username}</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>Password</ResultLabel>
                  <ResultValue>{showPassword ? result.password : '••••••••'}</ResultValue>
                  <CopyButton onClick={() => setShowPassword((s) => !s)}>
                    {showPassword ? 'Hide' : 'Show'}
                  </CopyButton>
                  <CopyButton onClick={() => copy('Password', result.password)}>Copy</CopyButton>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>Issued</ResultLabel>
                  <ResultValue>{formatWhen(result.issued_at)}</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>Expires</ResultLabel>
                  <ResultValue>{formatWhen(result.expires_at)}</ResultValue>
                </ResultRow>
              </ResultGrid>

              <SaveForm>
                <SaveTitle>Camera SIM</SaveTitle>
                <ToolDescription>Read the SIM ICCID from this camera and check whether it is in our Pelion account. This does not change camera settings.</ToolDescription>
                <Button onClick={onRetrieveSim} isLoading={simBusy} disabled={simBusy || result.username !== 'admin'}>
                  Retrieve SIM ICCID
                </Button>
                <div role="status" aria-live="polite" aria-atomic="true">
                  {simBusy && <p>Connecting to the camera and reading its SIM… This can take up to two minutes.</p>}
                  {simError && <p>{simError}</p>}
                  {result.username !== 'admin' && <p>SIM retrieval requires the camera administrator credentials.</p>}
                  {simLookup && <>
                    <ResultRow><ResultLabel>SIM ICCID</ResultLabel><ResultValue>{simLookup.iccid}</ResultValue><CopyButton onClick={() => copy('SIM ICCID', simLookup.iccid)}>Copy</CopyButton></ResultRow>
                    <p><strong>{simLookup.provider_status === 'managed_pelion' ? 'Farmstream-managed Pelion SIM' : simLookup.provider_status === 'not_found' ? 'Not found in our Pelion account' : 'Couldn’t verify SIM provider'}</strong></p>
                    {simLookup.provider_status === 'not_found' && <p>This may be a customer-supplied SIM or a SIM in another account. Ownership has not been confirmed.</p>}
                    {simLookup.provider_status === 'unverified' && <p>The ICCID was read successfully, but the Pelion check could not be completed. Try again later.</p>}
                  </>}
                </div>
              </SaveForm>

              <SaveForm>
                <SaveTitle>
                  {savedUid === result.uid ? 'Saved ✓ — edit and re-save if needed' : 'Save this camera'}
                </SaveTitle>
                <SaveFields>
                  <TextInput
                    placeholder="Nickname (e.g. Home Farm barn cam)"
                    value={nickname}
                    maxLength={100}
                    onChange={(e) => setNickname(e.target.value)}
                  />
                  <NotesInput
                    placeholder="Notes — location, customer, install details…"
                    value={notes}
                    rows={3}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                  <Button onClick={onSave} isLoading={isSaving}>
                    {savedUid === result.uid ? 'Re-save camera' : 'Save camera'}
                  </Button>
                </SaveFields>
              </SaveForm>
            </ResultPanel>
          )}
        </ToolCard>

        <ToolCard>
          <ToolTitle>Saved cameras</ToolTitle>
          <ToolDescription>
            Cameras the team has saved from decoded QRs. Search by UID or nickname, then view the
            password or mint a fresh 24h share QR. Passwords are stored encrypted.
          </ToolDescription>

          <SearchInput
            placeholder="Search by UID or nickname…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {isListing && cameras.length === 0 ? (
            <EmptyNote>Loading saved cameras…</EmptyNote>
          ) : cameras.length === 0 ? (
            <EmptyNote>
              {search
                ? 'No saved cameras match that search.'
                : 'Nothing saved yet — decode a share QR above and save it.'}
            </EmptyNote>
          ) : (
            <CameraList>
              {cameras.map((camera) => (
                <CameraRow key={camera.id}>
                  {editingId === camera.id ? (
                    <EditFields>
                      <TextInput
                        value={editNickname}
                        maxLength={100}
                        onChange={(e) => setEditNickname(e.target.value)}
                      />
                      <NotesInput
                        value={editNotes}
                        rows={3}
                        onChange={(e) => setEditNotes(e.target.value)}
                      />
                      <RowActions>
                        <Button onClick={onUpdate} isLoading={isUpdating}>
                          Save changes
                        </Button>
                        <CopyButton onClick={() => setEditingId(null)}>Cancel</CopyButton>
                      </RowActions>
                    </EditFields>
                  ) : (
                    <>
                      <CameraHead>
                        <div>
                          <CameraNickname>{camera.nickname}</CameraNickname>
                          <CameraUid onClick={() => copy('UID', camera.uid)} title="Click to copy">
                            {camera.uid}
                          </CameraUid>
                        </div>
                        <RowActions>
                          <CopyButton
                            onClick={() => onRowShareQr(camera)}
                            disabled={busyCameraId === camera.id}>
                            {rowQr?.id === camera.id ? 'Hide QR' : 'Share QR'}
                          </CopyButton>
                          <CopyButton onClick={() => onRevealPassword(camera)}>
                            {revealed[camera.id] !== undefined ? 'Hide password' : 'Show password'}
                          </CopyButton>
                          <CopyButton onClick={() => startEdit(camera)}>Edit</CopyButton>
                          <DeleteButton
                            armed={armedDelete === camera.id}
                            onClick={() => onDelete(camera)}
                            disabled={busyCameraId === camera.id}>
                            {armedDelete === camera.id ? 'Confirm delete' : 'Delete'}
                          </DeleteButton>
                        </RowActions>
                      </CameraHead>
                      {revealed[camera.id] !== undefined && (
                        <PasswordRow>
                          <PasswordLabel>Password</PasswordLabel>
                          <PasswordValue>{revealed[camera.id]}</PasswordValue>
                          <CopyButton onClick={() => copy('Password', revealed[camera.id])}>
                            Copy
                          </CopyButton>
                        </PasswordRow>
                      )}
                      {camera.notes && <CameraNotes>{camera.notes}</CameraNotes>}
                      <CameraMeta>
                        Saved {formatWhen(camera.created_at)}
                        {camera.created_by ? ` by ${camera.created_by}` : ''}
                        {camera.updated_at && camera.updated_at !== camera.created_at
                          ? ` · updated ${formatWhen(camera.updated_at)}`
                          : ''}
                      </CameraMeta>
                      {rowQr?.id === camera.id && (
                        <NewQrPanel>
                          <NewQrTitle>
                            Share QR — valid until {formatWhen(rowQr.qr.expires_at)}
                          </NewQrTitle>
                          <NewQrHint>
                            Scan with the Farmstream / CamHi app, or download and send it on.
                          </NewQrHint>
                          <NewQrImage
                            src={rowQr.qr.qr_image}
                            alt={`Share QR for ${camera.nickname}`}
                          />
                          <NewQrActions>
                            <DownloadLink
                              href={rowQr.qr.qr_image}
                              download={`share-qr-${camera.uid}.png`}>
                              Download PNG
                            </DownloadLink>
                          </NewQrActions>
                        </NewQrPanel>
                      )}
                    </>
                  )}
                </CameraRow>
              ))}
            </CameraList>
          )}
        </ToolCard>

        <BackLink onClick={() => navigate('/admin/select-user')}>← Back to admin</BackLink>
      </Card>
    </Page>
  );
};

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.gray};
  padding: 2rem 1rem;
  display: flex;
  justify-content: center;
`;

const Card = styled.div`
  width: 100%;
  max-width: 720px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  height: fit-content;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Logo = styled.img`
  height: 48px;
`;

const Title = styled.h1`
  font-size: 1.4rem;
  margin: 0;
  color: ${({ theme }) => theme.colors.gray900};
`;

const Subtitle = styled.p`
  margin: 0.15rem 0 0;
  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.fontSize.small};
`;

const ToolCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.gray};
  border-radius: 0.75rem;
  padding: 1.25rem;

  & + & {
    margin-top: 1.25rem;
  }
`;

const ToolTitle = styled.h2`
  margin: 0 0 0.25rem;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.gray900};
`;

const ToolDescription = styled.p`
  margin: 0 0 1rem;
  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.fontSize.small};
  line-height: 1.4;
`;

const DropZone = styled.div<{ active: boolean; busy: boolean }>`
  position: relative;
  border: 2px dashed
    ${({ theme, active }) => (active ? theme.colors.primary : theme.colors.gray500)};
  background: ${({ theme, active }) => (active ? theme.colors.primary100 : theme.colors.white)};
  border-radius: 0.75rem;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ busy }) => (busy ? 'progress' : 'pointer')};
  transition: 0.2s;
  overflow: hidden;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const DropHint = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  align-items: center;
  color: ${({ theme }) => theme.colors.gray900};
  padding: 2rem 1rem;
  text-align: center;

  span {
    color: ${({ theme }) => theme.colors.gray500};
    font-size: ${({ theme }) => theme.fontSize.extraSmall};
  }
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 320px;
  object-fit: contain;
`;

const BusyOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
`;

const HiddenInput = styled.input`
  display: none;
`;

const ResultPanel = styled.div`
  margin-top: 1.25rem;
`;

const ExpiredBanner = styled.div`
  background: #fff6e5;
  border: 1px solid ${({ theme }) => theme.colors.yellow900};
  color: ${({ theme }) => theme.colors.gray900};
  border-radius: 0.5rem;
  padding: 0.6rem 0.9rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  margin-bottom: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  align-items: flex-start;
`;

const NewQrPanel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.green900};
  background: #f2fcf5;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 0.9rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const NewQrTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
  font-size: ${({ theme }) => theme.fontSize.small};
`;

const NewQrHint = styled.div`
  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  text-align: center;
`;

const NewQrImage = styled.img`
  width: 260px;
  max-width: 100%;
  image-rendering: pixelated;
  border-radius: 0.25rem;
`;

const NewQrActions = styled.div`
  display: flex;
  gap: 0.6rem;
`;

const DownloadLink = styled.a`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 0.4rem;
  padding: 0.3rem 0.9rem;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  text-decoration: none;
  transition: 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary100};
  }
`;

const ResultGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: ${({ theme }) => theme.colors.gray};
  border-radius: 0.5rem;
  padding: 0.55rem 0.8rem;
`;

const ResultLabel = styled.div`
  width: 90px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ResultValue = styled.div`
  flex: 1;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', monospace;
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.gray900};
  word-break: break-all;
`;

const CopyButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 0.4rem;
  padding: 0.25rem 0.7rem;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary100};
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
`;

const DeleteButton = styled.button<{ armed: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.red};
  background: ${({ theme, armed }) => (armed ? theme.colors.red : 'transparent')};
  color: ${({ theme, armed }) => (armed ? theme.colors.white : theme.colors.red)};
  border-radius: 0.4rem;
  padding: 0.25rem 0.7rem;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  cursor: pointer;
  transition: 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
`;

const SaveForm = styled.div`
  margin-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.gray};
  padding-top: 1rem;
`;

const SaveTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
  font-size: ${({ theme }) => theme.fontSize.small};
  margin-bottom: 0.6rem;
`;

const SaveFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  align-items: flex-start;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500};
  border-radius: 0.5rem;
  font-size: ${({ theme }) => theme.fontSize.small};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const NotesInput = styled.textarea`
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500};
  border-radius: 0.5rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500};
  border-radius: 0.5rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  margin-bottom: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const EmptyNote = styled.p`
  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.fontSize.small};
  text-align: center;
  margin: 1rem 0 0.25rem;
`;

const CameraList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const CameraRow = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.gray};
  border-radius: 0.6rem;
  padding: 0.8rem 0.9rem;
`;

const CameraHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const CameraNickname = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
`;

const CameraUid = styled.div`
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', monospace;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  color: ${({ theme }) => theme.colors.gray500};
  cursor: pointer;
  margin-top: 0.15rem;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const PasswordRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: ${({ theme }) => theme.colors.gray};
  border-radius: 0.5rem;
  padding: 0.45rem 0.7rem;
  margin-top: 0.5rem;
`;

const PasswordLabel = styled.div`
  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const PasswordValue = styled.div`
  flex: 1;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', monospace;
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.gray900};
  word-break: break-all;
`;

const CameraNotes = styled.p`
  margin: 0.5rem 0 0;
  color: ${({ theme }) => theme.colors.gray900};
  font-size: ${({ theme }) => theme.fontSize.small};
  white-space: pre-wrap;
`;

const CameraMeta = styled.div`
  margin-top: 0.4rem;
  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  margin-bottom: 0.5rem;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.4rem;
  align-items: center;
  flex-wrap: wrap;
`;

const EditFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const BackLink = styled.button`
  margin-top: 1.25rem;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  padding: 0;
  font-size: ${({ theme }) => theme.fontSize.small};
`;
